<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  item: { type: Object, required: true },
  currency: { type: String, default: 'BRL' },
  disabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
});

const emit = defineEmits(['update-qty', 'remove']);

const { t } = useI18n();
const { localeTag } = useDateLocale();
const quantity = ref(props.item.quantity);
const isRemoving = ref(false);
let debounceTimer = null;

watch(
  () => props.item.quantity,
  val => { quantity.value = val; }
);

const onRemove = () => {
  isRemoving.value = true;
  emit('remove', props.item);
};

const onQtyChange = newQty => {
  if (newQty < 1) return;
  quantity.value = newQty;
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    emit('update-qty', { item: props.item, quantity: newQty });
  }, 400);
};

const formatPrice = amount =>
  amount != null
    ? new Intl.NumberFormat(localeTag.value, { style: 'currency', currency: props.currency }).format(amount)
    : '—';
</script>

<template>
  <div class="flex items-start gap-2 py-3 border-b border-n-weak last:border-0">
    <img
      v-if="item.image_url"
      :src="item.image_url"
      :alt="item.title"
      class="w-10 h-10 rounded object-cover shrink-0 bg-n-solid-3"
    />
    <div
      v-else
      class="w-10 h-10 rounded bg-n-solid-3 flex items-center justify-center shrink-0"
    >
      <i class="i-lucide-package text-n-slate-9 text-lg" />
    </div>

    <div class="flex-1 min-w-0">
      <p class="text-xs font-medium text-n-slate-12 leading-tight line-clamp-2">{{ item.title }}</p>
      <p v-if="item.variant_title" class="text-[11px] text-n-slate-11 truncate mt-0.5">
        {{ item.variant_title }}
      </p>
      <div class="flex items-center justify-between mt-1.5">
        <div v-if="!readonly" class="flex items-center gap-1">
          <button
            class="w-5 h-5 rounded flex items-center justify-center bg-n-solid-3 hover:bg-n-alpha-2 text-n-slate-11 disabled:opacity-40"
            :disabled="disabled || loading || quantity <= 1"
            @click="onQtyChange(quantity - 1)"
          >
            <span class="text-sm leading-none select-none">−</span>
          </button>
          <span class="w-5 text-center flex items-center justify-center">
            <i v-if="loading" class="i-lucide-loader-2 animate-spin text-n-slate-9 text-xs" />
            <span v-else class="text-xs font-medium text-n-slate-12">{{ quantity }}</span>
          </span>
          <button
            class="w-5 h-5 rounded flex items-center justify-center bg-n-solid-3 hover:bg-n-alpha-2 text-n-slate-11 disabled:opacity-40"
            :disabled="disabled || loading"
            @click="onQtyChange(quantity + 1)"
          >
            <span class="text-sm leading-none select-none">+</span>
          </button>
        </div>
        <span v-if="readonly" class="text-xs text-n-slate-11">{{
          t('CONTACTS_LAYOUT.SIDEBAR.CARTS.QUANTITY_TIMES', { count: quantity })
        }}</span>
        <span class="text-xs font-semibold text-n-slate-12">{{ formatPrice(item.unit_price) }}</span>
      </div>
    </div>

    <div v-if="!readonly" class="h-6 w-6 flex items-center justify-center shrink-0">
      <i
        v-if="isRemoving"
        class="i-lucide-loader-2 animate-spin text-n-ruby-11 text-sm"
      />
      <Button
        v-else
        v-tooltip="t('CONVERSATION.CART.ITEM_REMOVE')"
        ghost
        ruby
        xs
        icon="i-lucide-trash-2"
        :disabled="disabled"
        @click="onRemove"
      />
    </div>
  </div>
</template>
